"use client";

import { useMemo } from "react";
import { Plus, Trash2 } from "lucide-react";
import { FgInput } from "@/components/fg-input";
import { FgToggle } from "@/components/fg-toggle";
import { SortableList } from "../SortableList";
import { AiAssistButton } from "../AiAssistButton";
import { MediaUploader } from "../MediaUploader";
import { FormValidityBanner } from "../FormValidityBanner";
import { MobilePreview } from "../MobilePreview";

export interface ChoiceRow {
  id: string;
  /** "text" | "audio" | "image" — see Content.kind. */
  kind: "text" | "audio" | "image";
  /** The value: a literal string for text, a URL for audio/image. */
  value: string;
  isCorrect: boolean;
}

export interface MultipleChoiceBuilderProps {
  /** "multiple_choice" | "audio_mcq" */
  variant: "multiple_choice" | "audio_mcq";
  /** Prompt text (always required). */
  prompt: string;
  onPromptChange: (next: string) => void;
  /** Optional audio URL for the prompt (audio_mcq). */
  promptAudioUrl?: string;
  onPromptAudioUrlChange?: (next: string) => void;
  /** Choices list. */
  choices: ChoiceRow[];
  onChoicesChange: (next: ChoiceRow[]) => void;
  /** Optional explanation shown after grading. */
  explanation?: string;
  onExplanationChange?: (next: string) => void;
  /** CEFR level — drives AI suggestions. */
  level: "A1" | "A2" | "B1" | "B2" | "C1";
}

export function MultipleChoiceBuilder({
  variant,
  prompt,
  onPromptChange,
  promptAudioUrl,
  onPromptAudioUrlChange,
  choices,
  onChoicesChange,
  explanation,
  onExplanationChange,
  level,
}: MultipleChoiceBuilderProps) {
  const correctCount = choices.filter((c) => c.isCorrect).length;

  const validity = useMemo(
    () => [
      {
        label: "Prompt filled in",
        severity: prompt.trim() ? ("ok" as const) : ("error" as const),
      },
      {
        label: "At least 2 choices",
        severity:
          choices.length >= 2 ? ("ok" as const) : ("error" as const),
      },
      {
        label: "Exactly one choice marked correct",
        severity:
          correctCount === 1
            ? ("ok" as const)
            : correctCount === 0
              ? ("error" as const)
              : ("warn" as const),
      },
      {
        label: "Every choice has a value",
        severity: choices.every((c) => c.value.trim())
          ? ("ok" as const)
          : ("error" as const),
      },
    ],
    [prompt, choices, correctCount],
  );

  function addChoice() {
    onChoicesChange([
      ...choices,
      { id: crypto.randomUUID(), kind: "text", value: "", isCorrect: false },
    ]);
  }

  function removeChoice(id: string) {
    onChoicesChange(choices.filter((c) => c.id !== id));
  }

  function patchChoice(id: string, patch: Partial<ChoiceRow>) {
    onChoicesChange(
      choices.map((c) => (c.id === id ? { ...c, ...patch } : c)),
    );
  }

  function applyAiChoices(content: unknown) {
    if (!Array.isArray(content)) return;
    const start = choices.length;
    const next: ChoiceRow[] = [
      ...choices,
      ...content.map((text, i) => ({
        id: crypto.randomUUID(),
        kind: "text" as const,
        value: String(text),
        isCorrect: start === 0 && i === 0, // first suggestion gets marked correct if list was empty
      })),
    ];
    onChoicesChange(next);
  }

  return (
    <div className="flex flex-col gap-4">
      <FormValidityBanner title="Multiple choice" checks={validity} />

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-[1.6fr,1fr]">
        <div className="flex flex-col gap-3">
          <FgInput
            label="Prompt"
            value={prompt}
            onChange={(e) => onPromptChange(e.target.value)}
            placeholder={
              variant === "audio_mcq"
                ? "What does this audio say?"
                : "Was bedeutet \"Guten Morgen\"?"
            }
            required
          />
          {variant === "audio_mcq" && onPromptAudioUrlChange && (
            <MediaUploader
              kind="audio"
              value={promptAudioUrl ?? ""}
              onChange={onPromptAudioUrlChange}
              label="Prompt audio (optional but recommended)"
              hint="Plays before the choices are shown. Use a clear recording ≤ 10 seconds."
            />
          )}
        </div>
        <MobilePreview
          kind={variant}
          prompt={prompt}
          items={choices.map((c) => c.value)}
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-[11px] font-semibold uppercase tracking-wider text-[#8E8E93]">
            Choices ({choices.length})
          </label>
          <AiAssistButton
            kind="choices"
            prompt={prompt || "Aufgabe"}
            level={level}
            label={`Generate 4 distractors`}
            onApply={applyAiChoices}
            disabled={!prompt.trim()}
          />
        </div>
        <SortableList
          items={choices}
          getKey={(c) => c.id}
          onReorder={onChoicesChange}
          emptyState={
            <div className="text-center py-3">
              <p className="text-[11px] font-mono text-[#8E8E93]">
                No choices yet — add one below or click ✨ Generate distractors
              </p>
            </div>
          }
          renderRow={({ item: c, dragHandle }) => (
            <div className="flex items-center gap-2 w-full">
              {dragHandle}
              <FgToggle
                checked={c.isCorrect}
                onChange={(v) => patchChoice(c.id, { isCorrect: v })}
                label={c.isCorrect ? "✓" : "Correct"}
              />
              <select
                value={c.kind}
                onChange={(e) =>
                  patchChoice(c.id, {
                    kind: e.target.value as ChoiceRow["kind"],
                    // Reset value when switching kind.
                    value: e.target.value === c.kind ? c.value : "",
                  })
                }
                aria-label="Choice kind"
                className="h-9 rounded-md border border-[rgba(255,255,255,0.08)] bg-[#1C1C1E] px-2 text-xs text-[#FAFAFA]"
              >
                <option value="text">Text</option>
                <option value="audio">Audio</option>
                <option value="image">Image</option>
              </select>
              {c.kind === "text" ? (
                <input
                  type="text"
                  value={c.value}
                  onChange={(e) => patchChoice(c.id, { value: e.target.value })}
                  placeholder="Choice text"
                  aria-label={`Choice text`}
                  className="h-9 flex-1 rounded-md border border-[rgba(255,255,255,0.08)] bg-[#1C1C1E] px-3 text-sm text-[#FAFAFA] placeholder:text-[#8E8E93] focus:outline-none focus:border-[#FFD60A]"
                />
              ) : (
                <input
                  type="url"
                  value={c.value}
                  onChange={(e) => patchChoice(c.id, { value: e.target.value })}
                  placeholder={
                    c.kind === "audio"
                      ? "Audio URL or upload…"
                      : "Image URL or upload…"
                  }
                  aria-label={`Choice ${c.kind} URL`}
                  className="h-9 flex-1 rounded-md border border-[rgba(255,255,255,0.08)] bg-[#1C1C1E] px-3 text-sm text-[#FAFAFA] placeholder:text-[#8E8E93] focus:outline-none focus:border-[#FFD60A]"
                />
              )}
              <button
                type="button"
                onClick={() => removeChoice(c.id)}
                className="rounded-md p-1.5 text-[#FF453A] hover:bg-[#FF453A]/15"
                aria-label="Remove choice"
              >
                <Trash2 size={14} />
              </button>
            </div>
          )}
        />
        <FgInput
          className="mt-3"
          label="Explanation (optional)"
          value={explanation ?? ""}
          onChange={(e) => onExplanationChange?.(e.target.value)}
          placeholder="Shown after the learner answers — clarifies the right answer."
        />
        <button
          type="button"
          onClick={addChoice}
          className="mt-3 inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-[#1C1C1E] border border-dashed border-[rgba(255,255,255,0.12)] text-xs text-[#8E8E93] hover:text-[#FAFAFA] hover:border-[#FFD60A]/40 w-full justify-center"
        >
          <Plus size={14} />
          Add choice
        </button>
      </div>
    </div>
  );
}